import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  Search,
  X,
  LinkIcon,
  Package,
  AlertCircle,
  Loader2,
  PlusIcon,
} from "lucide-react";
import api from "../../lib/api";
import toast from "react-hot-toast";

interface EntityProduct {
  id: string;
  name: string;
  mrp: string;
  pack: string;
  mfr: string;
  code: string;
}

interface UpmProduct {
  sku_id: number;
  sku_name: string;
  sku_sub_category?: string;
  brand: string;
  manufacturer_name: string;
  mrp_price: number;
  salt_name?: string;
  strength?: string;
  flavour?: string;
  drug_form?: string;
  item_size?: string;
  unit_type?: string;
  potency?: string;
}

interface ProductMappingModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: EntityProduct | null;
  onProductLinked: (entityProductId: string, upmProduct: UpmProduct) => void;
  openNewProductModal?: () => void;
  showOnlyOtc?: boolean;
}

const ProductMappingModal: React.FC<ProductMappingModalProps> = ({
  isOpen,
  onClose,
  product,
  onProductLinked,
  openNewProductModal,
  showOnlyOtc = false,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<UpmProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const searchDebounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Auto-populate search with first word when modal opens (following reference implementation)
  useEffect(() => {
    if (isOpen && product) {
      // Extract first word from product name (following reference implementation)
      const productName = product.name || '';
      const firstWord = productName.trim().split(/\s+/)[0] || productName;

      // Reset previous results first
      setSearchResults([]);
      setSearchQuery(firstWord);
      // Trigger initial search
      searchUpmProducts(firstWord, 0);
    } else {
      // Reset when modal closes
      setSearchQuery("");
      setSearchResults([]);
      setPage(0);
      setHasMore(false);
    }
  }, [isOpen, product]);

  const searchUpmProducts = async (query: string, pageNum: number = 0) => {
    const trimmedQuery = query.trim();

    if (!trimmedQuery || trimmedQuery.length < 3) {
      setSearchResults([]);
      setHasMore(false);
      return;
    }

    setLoading(true);
    try {
      const response = await api.searchUpmProducts(
        trimmedQuery,
        pageNum,
        10,
        showOnlyOtc
      );

      if (response.success) {
        setSearchResults(response.data.products || []);
        setHasMore(response.data.hasMore || false);
        setPage(pageNum);
      } else {
        toast.error("Failed to search products");
      }
    } catch (error) {
      toast.error("Error searching products");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);

    // Clear existing timer
    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }

    // Set new timer for debounced search
    searchDebounceTimer.current = setTimeout(() => {
      searchUpmProducts(value, 0);
    }, 300);
  };

  const handleLinkProduct = async (upmProduct: UpmProduct) => {
    if (!product) return;

    try {
      const response = await api.createProductLink(
        product.id,
        upmProduct.sku_id.toString()
      );

      if (response.success) {
        toast.success("Product linked successfully");
        onProductLinked(product.id, upmProduct);
        onClose();
      } else {
        toast.error(response.message || "Failed to link product");
      }
    } catch (error) {
      toast.error("Error linking product");
    }
  };

  if (!isOpen || !product) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <Card
        className="w-full max-w-4xl max-h-[85vh] overflow-hidden border-0 shadow-2xl bg-slate-50"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <CardTitle className="text-white text-xl">
                Link Product to Master Database
              </CardTitle>
              <div className="mt-2 flex flex-row gap-8">
                <div className="flex flex-col min-w-[220px] max-w-lg">
                  <p className="text-blue-100 font-semibold">{product.name}</p>
                  <div className="flex flex-wrap gap-3 mt-1 text-sm text-blue-100/90">
                    {product.mrp && product.mrp !== "0" && (
                      <span className="flex items-center gap-1">
                        <span className="opacity-75">MRP:</span>
                        <span className="font-medium">₹{product.mrp}</span>
                      </span>
                    )}
                    {product.pack && product.pack !== "N/A" && (
                      <span className="flex items-center gap-1">
                        <span className="opacity-75">Pack:</span>
                        <span className="font-medium">{product.pack}</span>
                      </span>
                    )}
                    {product.mfr && (
                      <span className="flex items-center gap-1">
                        <span className="opacity-75">Mfr:</span>
                        <span className="font-medium">{product.mfr}</span>
                      </span>
                    )}
                    {product.code && (
                      <span className="flex items-center gap-1">
                        <span className="opacity-75">Code:</span>
                        <span className="font-medium">{product.code}</span>
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-1 justify-end items-start">
                  <Button
                    type="button"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm whitespace-nowrap"
                    aria-label="Add New Product"
                    tabIndex={0}
                    onClick={() => {
                      openNewProductModal?.();
                    }}
                  >
                    <PlusIcon className="w-4 h-4 mr-1" />
                    New Product
                  </Button>
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-white hover:bg-white/20"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          <div className="space-y-4">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (searchDebounceTimer.current) {
                      clearTimeout(searchDebounceTimer.current);
                    }
                    searchUpmProducts(searchQuery, 0);
                  }
                }}
                placeholder="Search master products by name, brand, or manufacturer..."
                className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Search Results */}
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {loading ? (
                <div className="text-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
                  <p className="mt-4 text-slate-600">Searching...</p>
                </div>
              ) : searchResults.length > 0 ? (
                <>
                  {searchResults.map((ump) => (
                    <div
                      key={ump.sku_id}
                      className="p-4 bg-white border border-slate-200 rounded-lg hover:shadow-md transition-all duration-200"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          {/* Product Name */}
                          <h3 className="font-semibold text-slate-900 text-lg mb-2">
                            {ump.sku_name}
                          </h3>

                          {/* Product Details Grid */}
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div className="space-y-1">
                              {ump.sku_sub_category && (
                                <div className="text-sm">
                                  <span className="text-slate-500">Category:</span>
                                  <span className="ml-2 text-slate-700 font-medium">
                                    {ump.sku_sub_category}
                                  </span>
                                </div>
                              )}
                              <div className="text-sm">
                                <span className="text-slate-500">Brand:</span>
                                <span className="ml-2 text-slate-700 font-medium">
                                  {ump.brand}
                                </span>
                              </div>
                              {ump.drug_form && (
                                <div className="text-sm">
                                  <span className="text-slate-500">Form:</span>
                                  <span className="ml-2 text-slate-700 font-medium">
                                    {ump.drug_form}
                                  </span>
                                </div>
                              )}
                            </div>

                            <div className="space-y-1">
                              <div className="text-sm">
                                <span className="text-slate-500">Manufacturer:</span>
                                <span className="ml-2 text-slate-700 font-medium">
                                  {ump.manufacturer_name}
                                </span>
                              </div>
                              {(ump.item_size || ump.unit_type) && (
                                <div className="text-sm">
                                  <span className="text-slate-500">Packing:</span>
                                  <span className="ml-2 text-slate-700 font-medium">
                                    {ump.item_size} {ump.unit_type}
                                  </span>
                                </div>
                              )}
                              {ump.strength && (
                                <div className="text-sm">
                                  <span className="text-slate-500">Strength:</span>
                                  <span className="ml-2 text-slate-700 font-medium">
                                    {ump.strength}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Salt Composition */}
                          {ump.salt_name && (
                            <div className="text-sm mb-2">
                              <span className="text-slate-500">Composition:</span>
                              <span className="ml-2 text-slate-700">
                                {ump.salt_name}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Price and Action */}
                        <div className="ml-4 text-right space-y-3">
                          <div className="text-2xl font-bold text-emerald-600">
                            ₹{ump.mrp_price}
                          </div>
                          <Button
                            onClick={() => handleLinkProduct(ump)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm whitespace-nowrap"
                          >
                            <LinkIcon className="w-4 h-4 mr-1" />
                            Link Product
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Pagination */}
                  {(page > 0 || hasMore) && (
                    <div className="mt-4 flex justify-between items-center">
                      <Button
                        onClick={() => searchUpmProducts(searchQuery, page - 1)}
                        disabled={page === 0 || loading}
                        variant="outline"
                      >
                        ← Previous
                      </Button>

                      <span className="text-sm text-gray-600">
                        Page {page + 1}
                      </span>

                      <Button
                        onClick={() => searchUpmProducts(searchQuery, page + 1)}
                        disabled={!hasMore || loading}
                        variant="outline"
                      >
                        Next →
                      </Button>
                    </div>
                  )}
                </>
              ) : searchQuery && searchQuery.trim().length < 3 ? (
                <div className="text-center py-12 text-slate-500">
                  <AlertCircle className="w-16 h-16 mx-auto text-amber-400 mb-4" />
                  <p className="font-medium text-slate-700">
                    Minimum 3 characters required
                  </p>
                  <p className="text-sm mt-1">
                    Please enter at least 3 characters to search
                  </p>
                </div>
              ) : searchQuery ? (
                <div className="text-center py-12 text-slate-500">
                  <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <p className="font-medium text-slate-700">
                    No master products found
                  </p>
                  <p className="text-sm mt-1">Try different search terms</p>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Search className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <p className="font-medium text-slate-700">
                    Search Master Products
                  </p>
                  <p className="text-sm mt-1">
                    Enter a product name, brand, or manufacturer to find matches
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductMappingModal;
